import React from 'react';
import { getBaseUrl } from '../services/baseUrl';
import { getAuthToken } from '../services/auth';

import type {
  PlanAssistantMessage,
  PlanStepOption,
  PossibleCause,
  ReferenceImageBlock,
  ReferenceImageBlockSection,
  ReferenceImageHit,
  StepNode,
} from './TSFADAnalysis.shared';

type StylesMap = Record<string, any>;

type ParsedResourceItem = {
  partNumber?: string | null;
  description: string;
  failurePercent: number | null;
};

type AskMode = 'quick' | 'custom';
type ReferenceImageFeedbackContext = {
  section?: ReferenceImageBlockSection;
  itemId?: string;
  sourceNodeIds?: string[];
  blockText?: string;
  anchorText?: string;
};

const dedupeReferenceImages = (images: ReferenceImageHit[] = []) => {
  const byId = new Map<string, ReferenceImageHit>();
  images.forEach(image => {
    const imageId = String(image?.id || '').trim();
    if (!imageId || byId.has(imageId)) return;
    byId.set(imageId, image);
  });
  return Array.from(byId.values());
};

const findNonOverlappingPhraseRange = (
  text: string,
  phrase: string,
  occupied: Array<{ start: number; end: number }>
) => {
  const source = String(text || '');
  const target = String(phrase || '').trim();
  if (!source || !target) return null;
  const lowerSource = source.toLowerCase();
  const lowerTarget = target.toLowerCase();
  let fromIndex = 0;
  while (fromIndex < lowerSource.length) {
    const start = lowerSource.indexOf(lowerTarget, fromIndex);
    if (start < 0) return null;
    const end = start + target.length;
    const overlaps = occupied.some(range => start < range.end && end > range.start);
    if (!overlaps) return { start, end };
    fromIndex = start + 1;
  }
  return null;
};

const normaliseBlockIdSegment = (value: unknown, fallback = 'item') => {
  const cleaned = String(value || '').trim() || fallback;
  return cleaned
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || fallback;
};

const buildCauseActionPlanItemId = (
  cause: PossibleCause | null | undefined,
  section: ReferenceImageBlockSection,
  key: string | number
) => [
  'tsfad',
  normaliseBlockIdSegment(cause?.id || cause?.shortTitle || 'cause', 'cause'),
  normaliseBlockIdSegment(section, 'section'),
  normaliseBlockIdSegment(String(key || 'item'), 'item')
].join(':');

function AuthenticatedReferenceImage({
  src,
  alt,
  style,
  placeholderStyle,
  placeholderText = 'No preview'
}: {
  src?: string | null;
  alt: string;
  style: React.CSSProperties;
  placeholderStyle: React.CSSProperties;
  placeholderText?: string;
}) {
  const [resolvedSrc, setResolvedSrc] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!src) {
      setResolvedSrc(null);
      setFailed(false);
      return undefined;
    }

    if (/^(?:data|blob):/i.test(src)) {
      setResolvedSrc(src);
      setFailed(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setResolvedSrc(null);
    setFailed(false);

    const fetchImage = async () => {
      const token = getAuthToken();
      const baseUrl = /^(?:https?:|data:|blob:)/i.test(src)
        ? ''
        : await getBaseUrl().catch(() => '');
      const requestUrl =
        baseUrl && !/^(?:https?:|data:|blob:)/i.test(src)
          ? `${baseUrl.replace(/\/+$/, '')}${src.startsWith('/') ? src : `/${src}`}`
          : src;
      const response = await fetch(requestUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error('Image unavailable');
      return response.blob();
    };

    fetchImage()
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setResolvedSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src) {
    return <div style={placeholderStyle}>{placeholderText}</div>;
  }

  if (failed) {
    return <div style={placeholderStyle}>Image unavailable</div>;
  }

  if (!resolvedSrc) {
    return <div style={placeholderStyle}>Loading image...</div>;
  }

  return <img src={resolvedSrc} alt={alt} style={style} />;
}

type TSFADTestModalProps = {
  open: boolean;
  styles: StylesMap;
  selectedCause: PossibleCause | null;
  onClose: () => void;
  isEditingProcedure: boolean;
  isInactivating: boolean;
  inactivationPanelRef: React.RefObject<HTMLDivElement | null>;
  inactivationTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  formatCauseTitle: (description: string, shortTitle?: string) => string;
  inactivationReason: string;
  setInactivationReason: React.Dispatch<React.SetStateAction<string>>;
  confirmInactivateCause: () => void;
  cancelInactivation: () => void;
  procedureDraft: string;
  setProcedureDraft: React.Dispatch<React.SetStateAction<string>>;
  repairDraft: string;
  setRepairDraft: React.Dispatch<React.SetStateAction<string>>;
  toolsDraft: string;
  setToolsDraft: React.Dispatch<React.SetStateAction<string>>;
  partsDraft: string;
  setPartsDraft: React.Dispatch<React.SetStateAction<string>>;
  notesDraft: string;
  setNotesDraft: React.Dispatch<React.SetStateAction<string>>;
  procedureEditNotes: string;
  setProcedureEditNotes: React.Dispatch<React.SetStateAction<string>>;
  saveProcedureEdits: () => void;
  cancelProcedureEdit: () => void;
  selectedVerificationSteps: StepNode[];
  selectedRepairSteps: StepNode[];
  planAssistantOpen: boolean;
  setPlanAssistantOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPlanStepKey: string;
  setSelectedPlanStepKey: React.Dispatch<React.SetStateAction<string>>;
  selectedPlanSteps: PlanStepOption[];
  askPlanAssistant: (mode: AskMode) => void;
  planAssistantLoading: boolean;
  selectedPlanStep: PlanStepOption | null;
  planAssistantActiveAsk: AskMode | null;
  planAssistantError: string | null;
  planAssistantMessages: PlanAssistantMessage[];
  formatPlanAssistantText: (value: string) => React.ReactNode;
  planAssistantQuestion: string;
  setPlanAssistantQuestion: React.Dispatch<React.SetStateAction<string>>;
  setPlanAssistantMessages: React.Dispatch<React.SetStateAction<PlanAssistantMessage[]>>;
  setPlanAssistantError: React.Dispatch<React.SetStateAction<string | null>>;
  setPlanAssistantActiveAsk: React.Dispatch<React.SetStateAction<AskMode | null>>;
  setPlanAssistantConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  parseResourceItem: (item: string) => ParsedResourceItem;
  testNotes: string;
  setTestNotes: React.Dispatch<React.SetStateAction<string>>;
  regeneratingCause: boolean;
  restoringCause: boolean;
  beginProcedureEdit: () => void;
  handleReportIssueFromProcedure: () => void;
  regenerateProcedure: () => void;
  restoreOriginalProcedure: () => void;
  handleReactivateCurrentCause: () => void;
  beginInactivation: () => void;
  onFlagReferenceImage?: (payload: {
    cause: PossibleCause;
    image: ReferenceImageHit;
    context?: ReferenceImageFeedbackContext | null;
  }) => void;
};

export const TSFADTestModal: React.FC<TSFADTestModalProps> = ({
  open,
  styles,
  selectedCause,
  onClose,
  isEditingProcedure,
  isInactivating,
  inactivationPanelRef,
  inactivationTextareaRef,
  formatCauseTitle,
  inactivationReason,
  setInactivationReason,
  confirmInactivateCause,
  cancelInactivation,
  procedureDraft,
  setProcedureDraft,
  repairDraft,
  setRepairDraft,
  toolsDraft,
  setToolsDraft,
  partsDraft,
  setPartsDraft,
  notesDraft,
  setNotesDraft,
  procedureEditNotes,
  setProcedureEditNotes,
  saveProcedureEdits,
  cancelProcedureEdit,
  selectedVerificationSteps,
  selectedRepairSteps,
  planAssistantOpen,
  setPlanAssistantOpen,
  selectedPlanStepKey,
  setSelectedPlanStepKey,
  selectedPlanSteps,
  askPlanAssistant,
  planAssistantLoading,
  selectedPlanStep,
  planAssistantActiveAsk,
  planAssistantError,
  planAssistantMessages,
  formatPlanAssistantText,
  planAssistantQuestion,
  setPlanAssistantQuestion,
  setPlanAssistantMessages,
  setPlanAssistantError,
  setPlanAssistantActiveAsk,
  setPlanAssistantConversationId,
  parseResourceItem,
  testNotes,
  setTestNotes,
  regeneratingCause,
  restoringCause,
  beginProcedureEdit,
  handleReportIssueFromProcedure,
  regenerateProcedure,
  restoreOriginalProcedure,
  handleReactivateCurrentCause,
  beginInactivation,
  onFlagReferenceImage,
}) => {
  const [previewImages, setPreviewImages] = React.useState<ReferenceImageHit[]>([]);
  const [previewImageIndex, setPreviewImageIndex] = React.useState(0);
  const [previewZoom, setPreviewZoom] = React.useState(100);
  const [previewFeedbackContext, setPreviewFeedbackContext] =
    React.useState<ReferenceImageFeedbackContext | null>(null);

  const openPreviewImages = React.useCallback(
    (
      images: ReferenceImageHit[],
      imageId?: string,
      context?: ReferenceImageFeedbackContext | null
    ) => {
      const gallery = dedupeReferenceImages(images.filter(img => img && img.id));
      if (gallery.length === 0) return;
      const requestedIndex = imageId ? gallery.findIndex(img => img.id === imageId) : 0;
      setPreviewZoom(100);
      setPreviewImageIndex(requestedIndex >= 0 ? requestedIndex : 0);
      setPreviewFeedbackContext(context || null);
      setPreviewImages(gallery);
    },
    []
  );

  const closePreviewImages = React.useCallback(() => {
    setPreviewImages([]);
    setPreviewImageIndex(0);
    setPreviewZoom(100);
    setPreviewFeedbackContext(null);
  }, []);

  const showPreviousPreviewImage = React.useCallback(() => {
    setPreviewZoom(100);
    setPreviewImageIndex(index => Math.max(0, index - 1));
  }, []);

  const showNextPreviewImage = React.useCallback(() => {
    setPreviewZoom(100);
    setPreviewImageIndex(index => Math.min(previewImages.length - 1, index + 1));
  }, [previewImages.length]);

  const selectPreviewImage = React.useCallback((index: number) => {
    setPreviewZoom(100);
    setPreviewImageIndex(index);
  }, []);

  const previewImage = previewImages[previewImageIndex] || null;
  const previewImageCount = previewImages.length;
  const causeReferenceImages = React.useMemo(
    () => {
      const images = Array.isArray(selectedCause?.referenceImages)
        ? selectedCause?.referenceImages || []
        : [];
      return dedupeReferenceImages(images);
    },
    [selectedCause]
  );
  const causeReferenceBlocks = React.useMemo(
    () => {
      if (!Array.isArray(selectedCause?.referenceImageBlocks)) return [];
      return selectedCause?.referenceImageBlocks || [];
    },
    [selectedCause]
  );

  const resolveReferenceBlock = React.useCallback(
    (section: ReferenceImageBlockSection, _text: string, itemId?: string) => {
      const targetItemId = String(itemId || '').trim();
      if (!targetItemId) return null;
      return (
        causeReferenceBlocks.find(candidate => {
          const candidateItemId = String(candidate?.itemId || '').trim();
          const candidateSection = candidate?.section;
          if (!candidateItemId || candidateItemId !== targetItemId) return false;
          return !candidateSection || candidateSection === section;
        }) || null
      );
    },
    [causeReferenceBlocks]
  );

  const resolveReferenceBlockImages = React.useCallback(
    (block: ReferenceImageBlock | null) => {
      if (!block || !Array.isArray(block.imageIds)) return [];
      return dedupeReferenceImages(
        block.imageIds
          .map(imageId =>
            causeReferenceImages.find(image => String(image.id) === String(imageId))
          )
          .filter((image): image is ReferenceImageHit => Boolean(image?.id))
      );
    },
    [causeReferenceImages]
  );

  const renderReferenceImageTextLinks = React.useCallback(
    (block: ReferenceImageBlock, blockImages: ReferenceImageHit[]) => {
      const text = String(block?.text || '');
      const blockImageIds = Array.isArray(block?.imageIds) ? block.imageIds.map(String) : [];
      const imageById = new Map(blockImages.map(image => [String(image.id), image]));
      const occupiedRanges: Array<{ start: number; end: number }> = [];
      const renderableAnchors = (Array.isArray(block?.anchors) ? block.anchors : [])
        .map((anchor, anchorIndex) => {
          const anchorText = String(anchor?.text || '').trim();
          if (!anchorText) return null;
          const anchorImageIds =
            Array.isArray(anchor?.imageIds) && anchor.imageIds.length > 0
              ? anchor.imageIds.map(String)
              : blockImageIds;
          const gallery = dedupeReferenceImages(
            anchorImageIds
              .map(imageId => imageById.get(String(imageId)))
              .filter((image): image is ReferenceImageHit => Boolean(image?.id))
          );
          if (gallery.length === 0) return null;
          const range = findNonOverlappingPhraseRange(text, anchorText, occupiedRanges);
          if (!range) return null;
          occupiedRanges.push(range);
          return {
            key: `${anchorText}:${anchorIndex}:${range.start}`,
            start: range.start,
            end: range.end,
            gallery
          };
        })
        .filter(
          (
            anchor
          ): anchor is {
            key: string;
            start: number;
            end: number;
            gallery: ReferenceImageHit[];
          } => Boolean(anchor)
        )
        .sort((left, right) => left.start - right.start);

      if (renderableAnchors.length === 0) {
        return {
          content: text,
          hasLinks: false
        };
      }

      const parts: React.ReactNode[] = [];
      let cursor = 0;
      renderableAnchors.forEach(anchor => {
        if (anchor.start > cursor) {
          parts.push(text.slice(cursor, anchor.start));
        }
        parts.push(
          <button
            type="button"
            key={`tsfad-image-anchor:${anchor.key}`}
            style={styles.inlineImageTextLink}
            onClick={() =>
              openPreviewImages(anchor.gallery, undefined, {
                section: block?.section,
                itemId: block?.itemId,
                sourceNodeIds: Array.isArray(block?.sourceNodeIds) ? block.sourceNodeIds : [],
                blockText: text,
                anchorText: text.slice(anchor.start, anchor.end)
              })
            }
          >
            {text.slice(anchor.start, anchor.end)}
          </button>
        );
        cursor = anchor.end;
      });
      if (cursor < text.length) {
        parts.push(text.slice(cursor));
      }

      return {
        content: parts,
        hasLinks: true
      };
    },
    [openPreviewImages, styles.inlineImageTextLink]
  );

  const renderActionPlanBlockText = React.useCallback(
    (section: ReferenceImageBlockSection, text: string, itemId?: string) => {
      const resolvedText = String(text || '').trim();
      if (!resolvedText) return '';
      const block = resolveReferenceBlock(section, resolvedText, itemId);
      const blockImages = resolveReferenceBlockImages(block);
      if (!block || blockImages.length === 0) return resolvedText;
      return renderReferenceImageTextLinks(block, blockImages).content;
    },
    [renderReferenceImageTextLinks, resolveReferenceBlock, resolveReferenceBlockImages]
  );

  const renderStepNodesWithImages = React.useCallback(
    (nodes: StepNode[], section: 'VERIFICATION' | 'REPAIR') => {
      const renderNodes = (list: StepNode[], path: number[] = []): React.ReactNode => (
        <ol style={styles.testingList}>
          {list.map((node, index) => {
            const nextPath = [...path, index + 1];
            const description = String(node?.description || '').trim();
            const itemId = buildCauseActionPlanItemId(
              selectedCause,
              section,
              node?.number || nextPath.join('.')
            );
            const block = resolveReferenceBlock(section, description, itemId);
            const blockImages = resolveReferenceBlockImages(block);
            const linkedText =
              block && blockImages.length > 0
                ? renderReferenceImageTextLinks(block, blockImages)
                : { content: description || '', hasLinks: false };

            return (
              <li key={`${node.number || index}-${description}`} style={{ marginBottom: '0.35rem' }}>
                <span style={styles.stepNumber}>{node.number}</span>
                <span>{linkedText.content}</span>
                {node.subSteps && node.subSteps.length > 0 && (
                  <div style={{ marginTop: '0.35rem', paddingLeft: '1.25rem' }}>
                    {renderNodes(node.subSteps, nextPath)}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      );

      return renderNodes(nodes);
    },
    [
      renderReferenceImageTextLinks,
      resolveReferenceBlock,
      resolveReferenceBlockImages,
      selectedCause,
      styles.stepNumber,
      styles.testingList
    ]
  );

  if (!open || !selectedCause) return null;

  return (
    <div style={styles.modal} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            {selectedCause.unverifiedPlaceholder ? 'Unverified Cause' : 'Action Plan'} —{' '}
            {selectedCause.shortTitle || selectedCause.description || 'Possible Cause'}
          </h3>
          <div style={{ color: '#6b7280', marginBottom: '0.5rem' }}>
            {renderActionPlanBlockText(
              'SUMMARY',
              selectedCause.description || selectedCause.summary || '',
              buildCauseActionPlanItemId(selectedCause, 'SUMMARY', 1)
            )}
          </div>
          {!selectedCause.unverifiedPlaceholder && (
            <div style={styles.modalEstimate}>
              Estimated Time: {selectedCause.estimatedTime || 0} minutes
            </div>
          )}
        </div>

        <div style={styles.modalBody}>
          {selectedCause.procedureRegeneratedAt &&
            !selectedCause.procedureEditedByTech &&
            !isEditingProcedure && (
              <div style={styles.inlineNotice}>
                Procedure regenerated
                {selectedCause.procedureRegeneratedAt
                  ? ` ${new Date(selectedCause.procedureRegeneratedAt).toLocaleString()}`
                  : ''}
                .
              </div>
            )}
          {selectedCause.procedureEditedByTech && !isEditingProcedure && (
            <div style={styles.inlineNotice}>
              Procedure last updated
              {selectedCause.procedureEditedAt
                ? ` ${new Date(selectedCause.procedureEditedAt).toLocaleString()}`
                : ' during this session'}
              .
              {selectedCause.procedureEditNotes && (
                <div style={{ marginTop: '0.25rem' }}>
                  Notes: {selectedCause.procedureEditNotes}
                </div>
              )}
            </div>
          )}
          {selectedCause.unverifiedPlaceholder && !isEditingProcedure && (
            <div style={styles.inlineNotice}>
              <strong>Unverified technician-suggested cause.</strong>{' '}
              {selectedCause.planUnavailableReason ||
                'No action plan was generated because supporting service information is not available.'}
              {selectedCause.knowledgeGapReason && (
                <div style={{ marginTop: '0.35rem' }}>{selectedCause.knowledgeGapReason}</div>
              )}
            </div>
          )}

          {isInactivating && (
            <div ref={inactivationPanelRef} id="inactivation-panel" style={styles.inactivationPanel}>
              <h4 style={{ marginTop: 0 }}>Inactivate this cause</h4>
              <p style={styles.secondaryText}>
                Explain why "
                {formatCauseTitle(selectedCause.description, selectedCause.shortTitle)}
                " should be removed from the active troubleshooting list.
              </p>
              <textarea
                ref={inactivationTextareaRef}
                style={styles.textarea}
                placeholder="Share what made this cause unhelpful..."
                value={inactivationReason}
                onChange={(e) => setInactivationReason(e.target.value)}
              />
              <div style={styles.buttonRow}>
                <button
                  style={{ ...styles.button, ...styles.dangerButton }}
                  onClick={confirmInactivateCause}
                >
                  Confirm Inactivation
                </button>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={cancelInactivation}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isEditingProcedure ? (
            <div style={styles.editPanel} id="edit-panel">
              <div style={styles.editSection}>
                <label style={styles.editLabel}>Verification Steps</label>
                <textarea
                  style={styles.textarea}
                  value={procedureDraft}
                  onChange={(e) => setProcedureDraft(e.target.value)}
                  placeholder="One step per line. Use numbering for sub-steps if needed."
                />
                <span style={styles.fieldHint}>
                  One step per line. Use numbering (e.g., 1, 1.1) for nested steps.
                </span>
              </div>

              <div style={styles.editSection}>
                <label style={styles.editLabel}>Repair Steps (optional)</label>
                <textarea
                  style={styles.textarea}
                  value={repairDraft}
                  onChange={(e) => setRepairDraft(e.target.value)}
                  placeholder="Repair or corrective actions, one per line."
                />
              </div>

              <div style={styles.editSection}>
                <label style={styles.editLabel}>Tools (optional)</label>
                <textarea
                  style={{ ...styles.textarea, minHeight: '100px' }}
                  value={toolsDraft}
                  onChange={(e) => setToolsDraft(e.target.value)}
                  placeholder="List required tools, one per line."
                />
              </div>

              <div style={styles.editSection}>
                <label style={styles.editLabel}>Parts (optional)</label>
                <textarea
                  style={{ ...styles.textarea, minHeight: '100px' }}
                  value={partsDraft}
                  onChange={(e) => setPartsDraft(e.target.value)}
                  placeholder="List required parts, one per line."
                />
              </div>

              <div style={styles.editSection}>
                <label style={styles.editLabel}>Notes (optional)</label>
                <textarea
                  style={{ ...styles.textarea, minHeight: '100px' }}
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Additional notes, cautions, or reminders."
                />
              </div>

              <div style={styles.editSection}>
                <label style={styles.editLabel}>Change Notes (required)</label>
                <textarea
                  style={{ ...styles.textarea, minHeight: '100px' }}
                  value={procedureEditNotes}
                  onChange={(e) => setProcedureEditNotes(e.target.value)}
                  placeholder="Explain what changed and why."
                />
              </div>

              <div style={styles.buttonRow}>
                <button
                  style={{ ...styles.button, ...styles.primaryButton }}
                  onClick={saveProcedureEdits}
                >
                  Save Updates
                </button>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={cancelProcedureEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {(selectedVerificationSteps.length > 0 || selectedRepairSteps.length > 0) && (
                <div style={styles.verificationReuseHint}>
                  If a verification step or repair step was already completed under another cause
                  and conditions are unchanged, record it as complete and reference the earlier
                  cause in notes.
                </div>
              )}
              {!selectedCause.unverifiedPlaceholder && (
              <div style={styles.planAssistantContainer}>
                <div style={styles.planAssistantHeader}>
                  <div>
                    <div style={styles.planAssistantTitle}>Ask About This Plan</div>
                    <div style={styles.planAssistantSub}>
                      Select a step for quick guidance, or ask your own question.
                    </div>
                  </div>
                  <button
                    style={{ ...styles.button, ...styles.secondaryButton, padding: '0.35rem 0.65rem' }}
                    onClick={() => setPlanAssistantOpen(prev => !prev)}
                  >
                    {planAssistantOpen ? 'Hide' : 'Show'}
                  </button>
                </div>
                {planAssistantOpen && (
                  <div style={styles.planAssistantBody}>
                    <select
                      style={styles.planAssistantSelect}
                      value={selectedPlanStepKey}
                      onChange={e => setSelectedPlanStepKey(e.target.value)}
                    >
                      <option value="">No specific step selected</option>
                      {selectedPlanSteps.map(step => (
                        <option key={step.key} value={step.key}>
                          {step.section} {step.number}: {step.description.slice(0, 90)}
                          {step.description.length > 90 ? '...' : ''}
                        </option>
                      ))}
                    </select>
                    <div style={styles.planAssistantActions}>
                      <button
                        style={{ ...styles.button, ...styles.primaryButton, padding: '0.45rem 0.75rem' }}
                        onClick={() => askPlanAssistant('quick')}
                        disabled={planAssistantLoading || !selectedPlanStep}
                      >
                        {planAssistantLoading && planAssistantActiveAsk === 'quick'
                          ? 'Asking...'
                          : 'Quick Ask Selected Step'}
                      </button>
                    </div>
                    {planAssistantError && (
                      <div style={styles.planAssistantError}>{planAssistantError}</div>
                    )}
                    {planAssistantMessages.length > 0 && (
                      <div style={styles.planAssistantMessages}>
                        {planAssistantMessages.map(message => (
                          <div
                            key={message.id}
                            style={{
                              ...styles.planAssistantMessage,
                              ...(message.role === 'assistant'
                                ? styles.planAssistantAssistant
                                : styles.planAssistantUser)
                            }}
                          >
                            <strong>{message.role === 'assistant' ? 'Assistant' : 'You'}:</strong>{' '}
                            {message.role === 'assistant'
                              ? formatPlanAssistantText(message.content)
                              : message.content}
                          </div>
                        ))}
                      </div>
                    )}
                    <input
                      style={styles.planAssistantInput}
                      value={planAssistantQuestion}
                      onChange={e => setPlanAssistantQuestion(e.target.value)}
                      placeholder="Ask your own question (e.g., be concise: what are the exact pass/fail criteria?)"
                    />
                    <div style={styles.planAssistantActions}>
                      <button
                        style={{ ...styles.button, ...styles.primaryButton, padding: '0.45rem 0.75rem' }}
                        onClick={() => askPlanAssistant('custom')}
                        disabled={planAssistantLoading || !planAssistantQuestion.trim()}
                      >
                        {planAssistantLoading && planAssistantActiveAsk === 'custom'
                          ? 'Asking...'
                          : 'Ask'}
                      </button>
                      <button
                        style={{ ...styles.button, ...styles.secondaryButton, padding: '0.45rem 0.75rem' }}
                        onClick={() => {
                          setPlanAssistantMessages([]);
                          setPlanAssistantError(null);
                          setPlanAssistantActiveAsk(null);
                          setPlanAssistantConversationId(null);
                        }}
                        disabled={planAssistantLoading || planAssistantMessages.length === 0}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
              )}
              <div style={styles.testProcedure}>
                {selectedVerificationSteps.length > 0 && (
                  <div>
                    <div style={styles.testingLabel}>Verification Steps</div>
                    {renderStepNodesWithImages(selectedVerificationSteps, 'VERIFICATION')}
                  </div>
                )}
                {selectedRepairSteps.length > 0 && (
                  <div style={{ marginTop: selectedVerificationSteps.length > 0 ? '1rem' : 0 }}>
                    <div style={styles.testingLabel}>Repair Steps</div>
                    {renderStepNodesWithImages(selectedRepairSteps, 'REPAIR')}
                  </div>
                )}
                {selectedVerificationSteps.length === 0 && selectedRepairSteps.length === 0 && (
                  <span>
                    {selectedCause.unverifiedPlaceholder
                      ? 'No action plan has been generated for this unverified cause. A technician may add a manual procedure using Edit Procedure.'
                      : 'No structured steps provided.'}
                  </span>
                )}
              </div>

              {selectedCause.requiredTools.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Suggested Tools:</strong>
                  <ul style={{ marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                    {selectedCause.requiredTools.map((tool, index) => (
                      <li key={index}>
                        {(() => {
                          const parsed = parseResourceItem(tool);
                          const displayText = `${parsed.partNumber || 'NO-PN'} | ${parsed.description}`;
                          return renderActionPlanBlockText(
                            'TOOLS',
                            displayText,
                            buildCauseActionPlanItemId(selectedCause, 'TOOLS', index + 1)
                          );
                        })()}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedCause.requiredParts.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Suggested Parts:</strong>
                  <ul style={{ marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                    {selectedCause.requiredParts.map((part, index) => (
                      <li key={index}>
                        {(() => {
                          const parsed = parseResourceItem(part);
                          const failureText =
                            parsed.failurePercent === null
                              ? 'Failure N/A'
                              : `Failure ${parsed.failurePercent}%`;
                          const displayText = `${parsed.partNumber || 'NO-PN'} | ${parsed.description} | ${failureText}`;
                          return renderActionPlanBlockText(
                            'PARTS',
                            displayText,
                            buildCauseActionPlanItemId(selectedCause, 'PARTS', index + 1)
                          );
                        })()}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedCause.planNotes && selectedCause.planNotes.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Notes:</strong>
                  <ul style={{ marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                    {selectedCause.planNotes.map((note, index) => (
                      <li key={index}>
                        {renderActionPlanBlockText(
                          'NOTES',
                          note,
                          buildCauseActionPlanItemId(selectedCause, 'NOTES', index + 1)
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {!isEditingProcedure && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Test Notes (Optional):
              </label>
              <textarea
                style={styles.textarea}
                placeholder="Add any observations or notes about this test..."
                value={testNotes}
                onChange={(e) => setTestNotes(e.target.value)}
                disabled={selectedCause.status === 'INACTIVE' || regeneratingCause}
              />
            </div>
          )}

          {selectedCause.status === 'INACTIVE' && (
            <div style={styles.inlineInfo}>
              This cause is inactive. Reactivate it to capture new test results.
            </div>
          )}
        </div>

        <div style={styles.modalFooter}>
          <button
            style={{
              ...styles.button,
              ...styles.editActionButton,
              ...(restoringCause || regeneratingCause ? styles.disabledButton : {})
            }}
            onClick={isEditingProcedure ? cancelProcedureEdit : beginProcedureEdit}
            disabled={restoringCause || regeneratingCause}
          >
            {isEditingProcedure ? 'Close Editing' : 'Edit Procedure'}
          </button>
          <button
            style={{
              ...styles.button,
              ...styles.reportButton,
              ...(restoringCause || regeneratingCause ? styles.disabledButton : {})
            }}
            onClick={handleReportIssueFromProcedure}
            disabled={restoringCause || regeneratingCause}
          >
            Report Documentation Issue
          </button>
          {!selectedCause.unverifiedPlaceholder && <button
            style={{
              ...styles.button,
              ...styles.regenerateButton,
              ...(restoringCause || regeneratingCause || isEditingProcedure
                ? styles.disabledButton
                : {})
            }}
            onClick={regenerateProcedure}
            disabled={restoringCause || regeneratingCause || isEditingProcedure}
          >
            {regeneratingCause ? 'Regenerating...' : 'Regenerate'}
          </button>}
          {!selectedCause.unverifiedPlaceholder && <button
            style={{
              ...styles.button,
              ...styles.restoreButton,
              ...(restoringCause || regeneratingCause || isEditingProcedure
                ? styles.disabledButton
                : {})
            }}
            onClick={restoreOriginalProcedure}
            disabled={restoringCause || regeneratingCause || isEditingProcedure}
          >
            Restore
          </button>}
          {selectedCause.status === 'INACTIVE' ? (
            <button
              style={{
                ...styles.button,
                ...styles.successButton,
                ...(restoringCause || regeneratingCause ? styles.disabledButton : {})
              }}
              onClick={handleReactivateCurrentCause}
              disabled={restoringCause || regeneratingCause}
            >
              Reactivate Cause
            </button>
          ) : (
            <button
              style={{
                ...styles.button,
                ...styles.dangerOutlineButton,
                ...(restoringCause || regeneratingCause ? styles.disabledButton : {})
              }}
              onClick={beginInactivation}
              disabled={restoringCause || regeneratingCause}
            >
              Inactivate Cause
            </button>
          )}
          <button
            style={{ ...styles.button, ...styles.footerCloseButton }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
        {previewImage && (
          <div style={styles.previewBackdrop}>
            <div
              style={styles.previewCard}
              role="dialog"
              aria-modal="true"
              aria-label="Reference image preview"
            >
              <div style={styles.previewHeader}>
                <div style={styles.previewHeaderMain}>
                  <div style={styles.previewTitleRow}>
                    <div style={{ fontWeight: 700 }}>Reference image</div>
                    {previewImageCount > 1 && (
                      <span style={styles.previewGalleryCount}>
                        {previewImageIndex + 1} / {previewImageCount}
                      </span>
                    )}
                  </div>
                  {previewImage.label && (
                    <div style={styles.previewImageLabel}>{previewImage.label}</div>
                  )}
                </div>
                <div style={styles.previewHeaderActions}>
                  {onFlagReferenceImage && previewImage && selectedCause && (
                    <button
                      type="button"
                      style={styles.previewClose}
                      onClick={() =>
                        onFlagReferenceImage({
                          cause: selectedCause,
                          image: previewImage,
                          context: previewFeedbackContext
                        })
                      }
                    >
                      Flag image
                    </button>
                  )}
                  <button
                    type="button"
                    style={styles.previewClose}
                    onClick={closePreviewImages}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div style={styles.previewControls}>
                {previewImageCount > 1 && (
                  <>
                    <button
                      type="button"
                      style={{
                        ...styles.previewControlButton,
                        ...(previewImageIndex === 0 ? styles.previewControlButtonDisabled : {})
                      }}
                      onClick={showPreviousPreviewImage}
                      disabled={previewImageIndex === 0}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      style={{
                        ...styles.previewControlButton,
                        ...(previewImageIndex >= previewImageCount - 1
                          ? styles.previewControlButtonDisabled
                          : {})
                      }}
                      onClick={showNextPreviewImage}
                      disabled={previewImageIndex >= previewImageCount - 1}
                    >
                      Next
                    </button>
                  </>
                )}
                <button
                  type="button"
                  style={styles.previewControlButton}
                  onClick={() => setPreviewZoom(value => Math.max(100, value - 25))}
                  aria-label="Zoom out"
                >
                  -
                </button>
                <input
                  type="range"
                  min={100}
                  max={300}
                  step={25}
                  value={previewZoom}
                  onChange={event => setPreviewZoom(Number(event.target.value))}
                  style={styles.previewZoomSlider}
                  aria-label="Image zoom"
                />
                <button
                  type="button"
                  style={styles.previewControlButton}
                  onClick={() => setPreviewZoom(value => Math.min(300, value + 25))}
                  aria-label="Zoom in"
                >
                  +
                </button>
                <button
                  type="button"
                  style={styles.previewControlButton}
                  onClick={() => setPreviewZoom(100)}
                >
                  100%
                </button>
                <span style={styles.previewZoomValue}>{previewZoom}%</span>
              </div>
              {previewImageCount > 1 && (
                <div style={styles.previewGalleryStrip}>
                  {previewImages.map((image, index) => (
                    <button
                      type="button"
                      key={image.id || `${index}`}
                      style={{
                        ...styles.previewGalleryThumbButton,
                        ...(index === previewImageIndex
                          ? styles.previewGalleryThumbButtonActive
                          : {})
                      }}
                      onClick={() => selectPreviewImage(index)}
                      aria-label={`Open reference image ${index + 1}`}
                    >
                      <AuthenticatedReferenceImage
                        src={image.fileUrl}
                        alt={image.label || 'reference image'}
                        style={styles.previewGalleryThumb}
                        placeholderStyle={styles.previewGalleryThumbPlaceholder}
                      />
                    </button>
                  ))}
                </div>
              )}
              <div style={styles.previewImageStage}>
                <div
                  style={{
                    ...styles.previewImageCanvas,
                    width: `${previewZoom}%`,
                    height: `${previewZoom}%`,
                    alignItems: previewZoom > 100 ? 'flex-start' : 'center',
                    justifyContent: previewZoom > 100 ? 'flex-start' : 'center'
                  }}
                >
                  <AuthenticatedReferenceImage
                    src={previewImage.fileUrl}
                    alt="Reference image"
                    style={styles.previewImage}
                    placeholderStyle={styles.previewPlaceholder}
                    placeholderText="No preview available"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
